import { useUrlGenerator } from '@folklore/routes';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';

import { createLayoutRow, createVideosRow } from '../../lib/blocks';

import { useSite } from '../../contexts/SiteContext';
import UrbaniaHeader from '../headers/UrbaniaHeader';
import RowTitle from '../partials/RowTitle';
import HomePageBlocks from './HomePageBlocks';

import styles from '../../styles/pages/home-page-urbania.module.css';

type HomePageUrbaniaProps = {
    brand: Brand;
    className?: string | null;
    [key: string]: unknown;
};

function HomePageUrbania({ brand, className = null, ...props }: HomePageUrbaniaProps) {
    const url = useUrlGenerator();
    const { handle: siteHandle } = useSite();
    const { sections = [] } = brand || {};
    const blocks = [
        {
            role: 'block',
            type: 'header_featured_list',
            brand,
            cardTheme: 'over-bottom-left',
            cardWithout: ['category'],
            count: 1,
            className: styles.featuredBlock,
            innerClassName: styles.featuredInner,
            cardClassName: styles.featuredCard,
            imageFetchPriority: 'high',
            query: {
                brand: brand.id,
                exclude_type: ['contest', 'newsletter'],
                placement: ['home_featured_urbania'],
            },
            uniqueDocuments: true,
        },
        createLayoutRow('1_1_1_4', {
            cardTheme: 'box-translucent',
            title: (
                <RowTitle
                    url={url('all', {
                        brand: brand.slug,
                    })}
                >
                    <FormattedMessage
                        defaultMessage="Les plus récents"
                        description="Section title"
                    />
                </RowTitle>
            ),
            query: {
                brand: brand.id,
                placement: ['home_featured_urbania'],
                exclude_type: ['newsletter'],
                // type: ['article', 'video'],
            },
            clickRef: 'home_new',
            loading: 'lazy',
            cardImageLoading: 'lazy',
            uniqueDocuments: true,
            withAds: true,
            adsTargeting: {
                placement: 'home_featured_urbania',
            },
        }),
        createLayoutRow('1_1_1_4', {
            cardTheme: 'box-translucent',
            title: (
                <RowTitle
                    url={url('popular', {
                        brand: brand.slug,
                    })}
                >
                    <FormattedMessage
                        defaultMessage="Les plus populaires"
                        description="Section title"
                    />
                </RowTitle>
            ),
            query: {
                brand: brand.id,
                // popular: 'week',
            },
            hook: 'popular',
            clickRef: 'home_popular',
            loading: 'lazy',
            cardImageLoading: 'lazy',
            uniqueDocuments: true,
        }),

        {
            role: 'block',
            type: 'header_featured_sidebar',
            presentation: 'row',
            itemsType: 'documents',
            title: <RowTitle url={url('micromags')}>Micromag URBANIA</RowTitle>,
            query: {
                type: 'micromag',
                collection: `micromag-${siteHandle}`,
            },
            loading: 'lazy',
            cardImageLoading: 'lazy',
            featuredCardType: 'featured-micromag',
            featuredCardTheme: 'left',
            featuredCardRatio: 'micromag',
            featuredCardWith: ['image-link', 'cover_image'],
            featuredCardWithout: ['link'],
            cardType: 'over',
            cardSize: 'big',
            cardTheme: 'outline',
            cardRatio: 'micromag',
            responsive: true,
            cardWidth: 200,
            // count: 9,
            cardWithout: ['text', 'type-icon'],
            withBorderRadius: false,
            featuredCardClassName: styles.featuredMicromag,
        },
        createVideosRow({
            title: (
                <RowTitle url={url('videos')}>
                    <FormattedMessage
                        defaultMessage="Vidéos récentes"
                        description="Section title"
                    />
                </RowTitle>
            ),
            query: {
                brand: brand.id,
                placement: 'videos_featured_urbania',
            },
            clickRef: 'home_videos',
            loading: 'lazy',
            cardImageLoading: 'lazy',
            cardWith: ['brand-icon'],
            cardWithout: ['category'],
            uniqueDocuments: true,
        }),
        {
            type: 'collections-row',
            presentation: 'grid',
            title: (
                <RowTitle url={url('videos')}>
                    <FormattedMessage defaultMessage="Séries vidéos" description="Section title" />
                </RowTitle>
            ),
            count: 8,
            query: {
                // brand: brand.id,
                placement: ['videos_collections_urbania'],
                type: 'serie',
            },
            loading: 'lazy',
            cardImageLoading: 'lazy',
            cardType: 'vertical',
            cardRatio: 'vertical',
            cardTheme: 'rounded',
            cardWidth: 200,
        },
        {
            type: 'collections-row',
            presentation: 'grid',
            // minColumns: 1,
            // maxColumns: 'auto',
            className: styles.collectionsRow,
            title: (
                <RowTitle url={url('podcasts')}>
                    <FormattedMessage defaultMessage="Balados" description="Section title" />
                </RowTitle>
            ),
            count: 3,
            query: {
                // brand: brand.id,
                placement: ['podcasts_collections_urbania'],
                type: 'serie_podcast',
            },
            loading: 'lazy',
            cardImageLoading: 'lazy',
            cardType: 'documents',
            cardRatio: 'square',
            cardSize: null,
            cardTheme: 'box-translucent',
            cardWidth: 300,
            autoColumns: true,
            cardProps: {
                listTitle: (
                    <RowTitle>
                        <FormattedMessage
                            defaultMessage="Derniers épisodes"
                            description="Section title"
                        />
                    </RowTitle>
                ),
                listProps: {
                    loading: 'lazy',
                    cardImageLoading: 'lazy',
                    cardWithout: ['category', 'serie'],
                    cardWith: ['episode'],
                },
                listQuery: {
                    type: 'podcast',
                },
            },
            cardWithout: ['text'],
        },

        ...sections
            .filter(({ hidden = false }) => !hidden)
            .map(({ id: sectionId, label, slug }) =>
                createLayoutRow('1_1_1_4', {
                    role: 'block',
                    title: (
                        <RowTitle url={url('section', { brand: brand.slug, section: slug })}>
                            {label}
                        </RowTitle>
                    ),
                    loading: 'lazy',
                    query: {
                        brand: brand.id,
                        section: sectionId,
                    },
                    clickRef: `home_section_${slug}`,
                    cardType: 'vertical',
                    cardTheme: 'box-translucent',
                    cardWithout: slug !== 'culture' ? ['category'] : [],
                    uniqueDocuments: slug !== 'actualites',
                }),
            ),
        {
            type: 'authors-row',
            presentation: 'grid',
            // minColumns: 1,
            // maxColumns: 'auto',
            autoColumns: true,
            className: styles.collectionsRow,
            titleClassName: styles.authorsTitle,
            title: <RowTitle>Dans l’univers URBANIA</RowTitle>,
            count: 6,
            query: {
                for: 'documents',
                order: 'count',
                person: true,
                // brand: brand.id,
                handle: [
                    'hugo-meunier',
                    'jean-bourbeau',
                    'benoit-lelievre',
                    'salome-maari',
                    'vanessa-destine',
                ],
            },
            loading: 'lazy',
            cardImageLoading: 'lazy',
            cardType: 'documents',
            cardRatio: 'square',
            cardSize: 'small',
            cardTheme: 'box-translucent',
            cardWidth: 250,
            clickRef: 'home_authors',
            cardProps: {
                listProps: {
                    loading: 'lazy',
                    cardImageLoading: 'lazy',
                },
                listCount: 4,
            },
            cardWithout: ['text'],
        },
        {
            type: 'brands-row',
            presentation: 'grid',
            // minColumns: 1,
            // maxColumns: 'auto',
            autoColumns: true,
            className: styles.collectionsRow,
            count: 4,
            query: {
                // brand: brand.id,
                handle: ['dehors', 'quatre95', 'mollo', 'periodelibre'],
            },
            loading: 'lazy',
            cardImageLoading: 'lazy',
            cardType: 'documents',
            cardRatio: 'square',
            cardSize: 'medium',
            cardTheme: 'box-translucent',
            cardWidth: 300,
            cardProps: {
                listProps: {
                    loading: 'lazy',
                    cardImageLoading: 'lazy',
                    hook: 'popular',
                },
                listCount: 5,
            },
            cardWithout: ['text'],
        },
    ];
    return (
        <HomePageBlocks
            {...props}
            brand={brand}
            blocks={blocks}
            header={<UrbaniaHeader className={styles.header} />}
            className={classNames([styles.container, className])}
            blocksClassName={styles.blocks}
        />
    );
}

export default HomePageUrbania;
